import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DinamicTitle } from '../utils/DinamicTitle';
import { http } from '../config/http';
import TypeWriter from 'typewriter-effect';
import logoKD from '../assets/logo_kd.png';
import { IconEye, IconEyeOff, IconLock, IconUser } from '@tabler/icons-react';

export default function Login() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPasswor] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  function handleOnChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  const { username, password } = formData;

  async function handleSubmitLogin(e) {
    e.preventDefault();
    const dataLogin = {
      username,
      password,
    };
    setIsLoading(true);
    try {
      const response = await http.post(`auth/login`, dataLogin);
      navigate('/');
      toast.success('Login sukses', { autoClose: 600 });
      localStorage.setItem('toko', response?.data?.user?.cabang?.nama_cabang);
      localStorage.setItem('token', response?.data?.access_token);
      localStorage.setItem('name', response?.data?.user?.name);
      localStorage.setItem('role', response?.data?.user?.role[0]);
      localStorage.setItem('tipe_surat', parseInt(response?.data?.user?.cabang?.tipe_surat));
      localStorage.setItem('id_brankas', response?.data?.brankas[0]?.id);
    } catch (error) {
      toast.error(error.response?.data?.error, { autoClose: 600 });
    }
    setIsLoading(false);
  }
  DinamicTitle('Login || Komaladewi');

  const tahunCopy = new Date().getFullYear();
  const tahunDibuat = '2023';

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-amber-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300 px-4 py-6">

      {/* Animated Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-amber-400/10 dark:bg-amber-500/10 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-amber-300/10 dark:bg-amber-600/10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] rounded-full bg-amber-200/10 dark:bg-amber-400/5 blur-3xl" />

        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(245,158,11,0.5) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Main Card */}
      <div className="relative z-10 w-full max-w-md lg:max-w-5xl mx-auto flex flex-col lg:flex-row rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl shadow-amber-200/30 dark:shadow-black/40 border border-amber-200/50 dark:border-amber-900/20 transition-colors duration-300">

        {/* Left Panel - Branding (Hidden on mobile, shown on lg) */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-amber-700/90 via-amber-600/80 to-amber-800/90 dark:from-amber-900/90 dark:via-amber-800/80 dark:to-amber-900/90 backdrop-blur-xl p-8 lg:p-12 flex-col justify-center items-center relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-300/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-200/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

          {/* Logo */}
          <div className="relative z-10 mb-6">
            <div className="w-36 h-36 lg:w-44 lg:h-44 rounded-full overflow-hidden border-4 border-amber-300/30 shadow-xl shadow-amber-800/50 bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <img src={logoKD} alt="Komaladewi" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Title */}
          <h1 className="relative z-10 text-3xl lg:text-4xl font-bold text-amber-50 text-center tracking-tight mb-3">
            Komaladewi
          </h1>

          {/* Typewriter */}
          <div className="relative z-10 text-amber-200/80 text-lg font-medium text-center h-8">
            <TypeWriter
              options={{
                autoStart: true,
                loop: true,
                delay: 40,
                strings: ['Selamat Datang', 'Welcome'],
              }}
            />
          </div>

          {/* Description */}
          <p className="relative z-10 text-amber-100/50 text-sm text-center mt-6 max-w-sm leading-relaxed">
            Aplikasi Transaksi Penjualan dan Pembelian Emas dan Perhiasan{' '}
            <span className="text-amber-300/70 font-semibold">Bersurat</span>
          </p>

          {/* Decorative line */}
          <div className="relative z-10 mt-8 flex items-center gap-3 opacity-40">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-300" />
            <div className="w-2 h-2 rounded-full bg-amber-300" />
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-300" />
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="w-full lg:w-1/2 bg-white dark:bg-white/[0.03] dark:backdrop-blur-2xl p-6 sm:p-8 lg:p-12 flex flex-col justify-center transition-colors duration-300">

          {/* Mobile Logo (visible only on small screens) */}
          <div className="lg:hidden flex flex-col items-center mb-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-3 border-amber-300/50 dark:border-amber-400/30 shadow-lg shadow-amber-200/40 dark:shadow-amber-900/30 bg-amber-50 dark:bg-white/10 flex items-center justify-center mb-3">
              <img src={logoKD} alt="Komaladewi" className="w-full h-full object-cover" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-amber-700 dark:text-amber-400 tracking-tight">Komaladewi</h2>
            <div className="text-amber-500/60 dark:text-amber-300/60 text-sm font-medium h-6 mt-1">
              <TypeWriter
                options={{
                  autoStart: true,
                  loop: true,
                  delay: 40,
                  strings: ['Selamat Datang', 'Welcome'],
                }}
              />
            </div>
          </div>

          {/* Form Header */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 dark:text-white mb-1.5 transition-colors">Sign In</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Silahkan masuk ke akun Anda</p>
          </div>

          <form onSubmit={handleSubmitLogin} className="space-y-4 sm:space-y-5">
            {/* Username */}
            <div className="space-y-1.5 sm:space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-300 tracking-wide flex items-center gap-2">
                <IconUser size={16} className="text-amber-500/70" />
                Username
              </label>
              <input
                type="text"
                name="username"
                autoComplete="off"
                onChange={handleOnChange}
                required
                placeholder="Masukkan Username"
                className="w-full bg-slate-50 dark:bg-white/[0.06] border border-slate-200 dark:border-slate-600/50 rounded-xl px-4 py-3 sm:py-3.5 text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-amber-400 dark:focus:border-amber-500/60 focus:ring-2 focus:ring-amber-200/50 dark:focus:ring-amber-500/20 focus:bg-white dark:focus:bg-white/[0.08] transition-all duration-300 outline-none text-sm"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5 sm:space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-300 tracking-wide flex items-center gap-2">
                <IconLock size={16} className="text-amber-500/70" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  onChange={handleOnChange}
                  required
                  autoComplete="off"
                  placeholder="Masukkan Password"
                  className="w-full bg-slate-50 dark:bg-white/[0.06] border border-slate-200 dark:border-slate-600/50 rounded-xl px-4 py-3 sm:py-3.5 pr-12 text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-amber-400 dark:focus:border-amber-500/60 focus:ring-2 focus:ring-amber-200/50 dark:focus:ring-amber-500/20 focus:bg-white dark:focus:bg-white/[0.08] transition-all duration-300 outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswor(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-amber-500 dark:hover:text-amber-400 transition-colors p-1"
                >
                  {showPassword ? <IconEyeOff size={20} /> : <IconEye size={20} />}
                </button>
              </div>
            </div>

            {/* Show Password Checkbox */}
            <div className="flex items-center gap-2">
              <input
                id="passwordShow"
                type="checkbox"
                onClick={() => setShowPasswor(!showPassword)}
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-white/10 text-amber-500 focus:ring-amber-400/30 dark:focus:ring-amber-500/30 cursor-pointer"
              />
              <label
                htmlFor="passwordShow"
                className="text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              >
                Lihat Password
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 sm:py-3.5 px-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold rounded-xl shadow-lg shadow-amber-400/25 dark:shadow-amber-500/25 hover:shadow-amber-400/40 dark:hover:shadow-amber-500/40 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed group text-sm sm:text-base"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg
                    className="w-5 h-5 group-hover:translate-x-0.5 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-100 dark:border-slate-700/30 text-center">
            <p className="text-slate-400 dark:text-slate-500 text-xs">
              Copyright © {tahunDibuat} - {tahunCopy === tahunDibuat ? tahunCopy : tahunCopy}{' '}
              <Link to="#" className="text-amber-600/70 dark:text-amber-500/70 hover:text-amber-500 dark:hover:text-amber-400 transition-colors font-medium">
                Komala Dewi
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
